import React, { useMemo, useRef, useEffect } from "react";

interface SmokePuff {
  id: number;
  left: number;
  size: number;
  delay: number;
  duration: number;
  drift: number;
  opacity: number;
}

interface SmokeBackgroundProps {
  className?: string;
  count?: number;
  color?: string;
}

/**
 * Dark Ages hero effect: low-hanging hearth smoke curling up from the
 * bottom of the banner. Soft grey puffs rise slowly with gentle horizontal
 * drift, evoking smouldering long-hall fires and damp fenland mist.
 */
const SmokeBackground: React.FC<SmokeBackgroundProps> = ({
  className = "text-teal-800",
  count = 14,
  color = "rgba(190, 180, 165, 0.35)",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      // Normalize to -1..1 around center
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      targetRef.current = { x: nx, y: ny };
    };
    const handleLeave = () => {
      targetRef.current = { x: 0, y: 0 };
    };

    window.addEventListener("mousemove", handleMove);
    container.addEventListener("mouseleave", handleLeave);

    let raf = 0;
    const tick = () => {
      const cur = currentRef.current;
      const tgt = targetRef.current;
      cur.x += (tgt.x - cur.x) * 0.06;
      cur.y += (tgt.y - cur.y) * 0.06;
      if (layerRef.current) {
        // Subtle parallax: ~18px horizontal, ~8px vertical
        layerRef.current.style.transform = `translate3d(${cur.x * 18}px, ${cur.y * 8}px, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      container.removeEventListener("mouseleave", handleLeave);
      cancelAnimationFrame(raf);
    };
  }, []);

  const puffs = useMemo<SmokePuff[]>(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 140 + Math.random() * 220,
        delay: -(Math.random() * 18),
        duration: 14 + Math.random() * 14,
        drift: -60 + Math.random() * 120,
        opacity: 0.25 + Math.random() * 0.45,
      })),
    [count]
  );

  return (
    <div
      ref={containerRef}
      className={`overflow-hidden relative ${className}`}
      style={{
        WebkitMaskImage:
          "linear-gradient(to bottom, black 0%, black 70%, transparent 100%)",
        maskImage:
          "linear-gradient(to bottom, black 0%, black 70%, transparent 100%)",
      }}
    >
      {/* Warm hearth glow along the bottom edge */}
      <div
        className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 100%, rgba(220, 130, 60, 0.18) 0%, rgba(120, 60, 30, 0.08) 35%, transparent 70%)",
          mixBlendMode: "screen",
        }}
      />

      <div
        ref={layerRef}
        className="absolute inset-0 pointer-events-none"
        style={{ transition: "transform 0.1s linear", willChange: "transform" }}
      >
        {puffs.map((p) => (
          <span
            key={p.id}
            className="absolute rounded-full"
            style={{
              left: `${p.left}%`,
              bottom: `-${p.size * 0.4}px`,
              width: p.size,
              height: p.size,
              background: `radial-gradient(circle at 50% 50%, ${color} 0%, rgba(190,180,165,0.12) 45%, transparent 70%)`,
              filter: "blur(18px)",
              opacity: 0,
              animation: `smoke-rise ${p.duration}s ease-in-out ${p.delay}s infinite`,
              ["--smoke-opacity" as string]: `${p.opacity}`,
              ["--smoke-drift" as string]: `${p.drift}px`,
              willChange: "transform, opacity",
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes smoke-rise {
          0% {
            transform: translate(0, 0) scale(0.6);
            opacity: 0;
          }
          15% {
            opacity: var(--smoke-opacity, 0.4);
          }
          60% {
            transform: translate(calc(var(--smoke-drift) * 0.6), -180px) scale(1);
          }
          85% {
            opacity: calc(var(--smoke-opacity, 0.4) * 0.5);
          }
          100% {
            transform: translate(var(--smoke-drift), -340px) scale(1.4);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default SmokeBackground;